import { graphql, useFragment } from 'react-relay';

import { directoryProfileTitle } from '@/pages/directory/utils/directory-profile-heading';
import type { DirectoryTownSummary } from '@/sanity/directory';
import {
  absoluteUrl,
  buildBreadcrumbListSchema,
  buildPageMeta,
  buildRealEstateAgentSchema,
  buildStructuredDataScript,
} from '@/utils/seo';

import type { DirectoryAgentSeoHead_agent$key } from './__generated__/DirectoryAgentSeoHead_agent.graphql';
import DirectorySeoHead from './DirectorySeoHead';
import { findTownForAreas } from '../utils/town-for-areas';

type DirectoryAgentSeoHeadProps = {
  agent: DirectoryAgentSeoHead_agent$key;
  towns: readonly DirectoryTownSummary[];
};

const DirectoryAgentSeoHead = ({
  agent: agentKey,
  towns,
}: DirectoryAgentSeoHeadProps) => {
  const agent = useFragment(
    graphql`
      fragment DirectoryAgentSeoHead_agent on LandlordDirectoryAgent {
        slug
        name
        postcode
        address
        phone
        website
        googleMapsUrl
        areas {
          slug
        }
      }
    `,
    agentKey,
  );

  if (!agent.slug) {
    return null;
  }

  const path = `/agents/${encodeURIComponent(agent.slug)}`;
  const agentName = agent.name ?? 'Letting agent';
  const profileTitle = directoryProfileTitle(agentName, agent.postcode);
  const town = findTownForAreas(
    towns,
    agent.areas?.map((area) => area?.slug) ?? [],
  );
  const pageMeta = buildPageMeta({
    title: profileTitle,
    description: `See published fees, services, reviews and compliance information for ${agentName}, with links to every source.`,
    path,
  });
  const breadcrumbs = [
    { name: 'Letting agents', url: absoluteUrl('/letting-agents') },
  ];

  if (town) {
    breadcrumbs.push({
      name: town.name,
      url: absoluteUrl(`/letting-agents/${encodeURIComponent(town.slug)}`),
    });
  }
  breadcrumbs.push({ name: agentName, url: absoluteUrl(path) });

  return (
    <DirectorySeoHead
      pageMeta={pageMeta}
      scripts={[
        buildStructuredDataScript(buildBreadcrumbListSchema(breadcrumbs)),
        buildStructuredDataScript(
          buildRealEstateAgentSchema({
            name: agentName,
            url: absoluteUrl(path),
            address: agent.address,
            telephone: agent.phone,
            website: agent.website,
            googleMapsUrl: agent.googleMapsUrl,
          }),
        ),
      ]}
    />
  );
};

export default DirectoryAgentSeoHead;
